/*
 * tile-shader-specializer.ts — assemble the tile fragment shader
 * for a given layer stack
 *
 * A style's layer stack is fixed per draw, not per fragment, so
 * the composition it describes is baked into straight-line GLSL
 * whose opcodes are compile-time constants. The generated main()
 * flattens the layer stack into scope-level registers and calls
 * the shared opcode helper functions. The UBO still carries the
 * per-draw dynamic values (texture transforms, blend alphas,
 * constant colors), so no per-tile recompilation is needed —
 * programs are keyed by the structural shape of the layer stack.
 *
 * The template owns GLSL structure, expression composition and the
 * register stacks. This module supplies per-layer expansion data.
 */


import ShaderTemplate from './shader-template';
import specializerTemplate from './shaders/tile.frag.template.glsl';


/** Structural description of one active layer. */
export type LayerDesc = {
    target: 'color' | 'normal';
    source:
        | 'constant' | 'texture' | 'shade' | 'pop'
        | 'atm-density' | 'none';
    operation:
        | 'push' | 'blend' | 'atm-color' | 'shadows';

    srcShadeType?: 'diffuse' | 'specular';
    srcTextureUVs?: 'internal' | 'external';
    srcTextureSampling?: 'raw' | 'normal';
    srcTextureMaskIdx?: number;

    opBlendMode?:
        | 'overlay' | 'add' | 'multiply'
        | 'specular-multiply';

    srcTextureIdx?: number;
    flagMask: number;
}


/**
 * Cache key from the layer-stack shape and the render flags the
 * stack refers to.
 * @param renderFlags the frame's `Renderer.RenderFlags` bits
 */
export function layerSignature(
    layers: LayerDesc[], renderFlags: number,
): string {

    const parts: string[] = [];

    for (const layer of layers) {

        // a layer left out of the program contributes no shape
        if (!isActive(layer, renderFlags)) {

            parts.push('-');
            continue;
        }

        let key =
            `${layer.target[0]}`
            + `${layer.source[0]}`
            + `${layer.operation[0]}`;

        if (layer.source === 'shade')
            key += layer.srcShadeType![0];

        if (layer.source === 'texture') {
            key += layer.srcTextureUVs![0]
            + layer.srcTextureSampling![0];
            key += layer.srcTextureIdx;
            if (layer.srcTextureMaskIdx! >= 0)
            key += `m${layer.srcTextureMaskIdx}`;
        }

        if (layer.operation === 'blend')
            key += layer.opBlendMode![0];

        parts.push(key);
    }

    return parts.join('|');
}


/**
 * Build expansion data without assembling GLSL expressions or statements.
 * A layer whose `flagMask` bits are not all set in `renderFlags` is left
 * out of the program.
 * @param renderFlags the frame's `Renderer.RenderFlags` bits
 */
export function specializeFragmentSource(
    layers: LayerDesc[], discard: boolean, renderFlags: number,
): string {

    const active = layers.map(layer => isActive(layer, renderFlags));
    const records = layers.flatMap((layer, idx): ShaderTemplate.Data[] => {

        // idx stays the layer's index in the layer ubo
        if (!active[idx]) return [];

        // the target selects the color or the normal variant of a snippet
        const color = layer.target === 'color';
        const source: ShaderTemplate.Data = {};
        const operation: ShaderTemplate.Data = {};

        if (layer.source !== 'pop') source[layer.source] = {};
        if (layer.source === 'pop') source[color ? 'popc' : 'popn'] = {};
        if (layer.source === 'shade') source.shade = {
            specular: layer.srcShadeType === 'specular',
        };
        if (layer.source === 'texture') source.texture = {
            texIdx: layer.srcTextureIdx!,
            maskIdx: layer.srcTextureMaskIdx !== undefined
                && layer.srcTextureMaskIdx >= 0
                ? layer.srcTextureMaskIdx : -1,
            normalSampling: layer.srcTextureSampling === 'normal',
            coordinates: {
                [layer.srcTextureUVs === 'internal'
                    ? 'internal' : 'external']: {},
            },
        };

        if (layer.operation === 'push')
            operation[color ? 'pushc' : 'pushn'] = {};
        if (layer.operation === 'blend') {

            const mode = layer.opBlendMode!;
            operation.blend = {
                stack: color ? 'c' : 'n',
                method: mode === 'specular-multiply'
                    ? { [mode]: {} }
                    : { standard: { mode: { [mode]: {} } } },
            };
        }
        if (layer.operation === 'atm-color' || layer.operation === 'shadows')
            operation[layer.operation] = {};

        return [{
            idx, source, operation,
            whitewash: layer.target === 'color' ? {} : [],
        }];
    });

    // without a color push the color stack is empty and the output black
    const colored = layers.some((layer, idx) => active[idx]
        && layer.target === 'color' && layer.operation === 'push');

    return template.expand({
        maskUniforms: discard ? {} : [],
        discard: discard ? {} : [],
        layers: records,
        finalColor: colored ? { color: {} } : { black: {} },
    });
}


/** True when every render flag the layer requires is set. */
function isActive(layer: LayerDesc, renderFlags: number): boolean {

    return (renderFlags & layer.flagMask) === layer.flagMask;
}


const template = new ShaderTemplate(specializerTemplate);
